import { apm } from '@elastic/apm-rum';
import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { IMTokens } from '@/interface/IMTokens';
import { OddApiStatus } from '@/interface/enum';
import type { PlayerType } from '@/interface/enum';
import Api from '@/libraries/api';
import { UnexpectedResponseError } from '@/libraries/apiError';
import useState from '@/libraries/composables/useState';
import { formatMTokens } from '@/libraries/helpers/formatMToken';
import { getSportsBsiUrl } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export interface ICustomerSettings {
    isEFootballEnabled: boolean;
    isSimulationEnabled: boolean;
    isCricketEnabled: boolean;
    playerType: PlayerType;
    isInEFootballLeagueBlackList: boolean;
}

const GET_TOKEN_RETRY_COUNT = 5;

const createEmptyMTokens = (): IMTokens => ({ m: '', b: '', s: '' });

const wait = (ms: number) => new Promise((r) => {
    window.setTimeout(r, ms);
});

async function withRetry<T>(fn: () => Promise<T>, retries: number, delay = 0, error: Error | null = null): Promise<T> {
    if (retries === 0) {
        return Promise.reject(error ?? new Error('Retry limit exceeded'));
    }
    return fn().catch(err => wait(delay).then(() => withRetry(fn, (retries - 1), delay, err)));
}

export const useOddsApiStore = defineStore('oddsApi', () => {
    const [authToken, setAuthToken] = useState('');
    const [soccerMOddsTokens, setSoccerMOddsTokens] = useState<IMTokens>(createEmptyMTokens());
    const [othersMOddsTokens, setOthersMOddsTokens] = useState<IMTokens>(createEmptyMTokens());
    const [oddsApiStatus, setOddsApiStatus] = useState<OddApiStatus>(OddApiStatus.NO_TOKEN);

    const { isCricketEnabled, isInEFootballLeagueBlackList } = storeToRefs(useFeatureToggleStore());
    const { customerInfo } = storeToRefs(useAccountStore());

    const customerSettings = computed<ICustomerSettings>(() => ({
        isEFootballEnabled: customerInfo.value.isEFootballEnabled,
        isSimulationEnabled: customerInfo.value.isSimulationEnabled,
        isCricketEnabled: isCricketEnabled.value,
        playerType: customerInfo.value.playerType,
        isInEFootballLeagueBlackList: isInEFootballLeagueBlackList.value,
    }));

    async function refreshOddsApiTokens() {
        setOddsApiStatus(OddApiStatus.LOADING);
        try {
            const tokens = await withRetry(() => Api.getOddsApiTokens(), GET_TOKEN_RETRY_COUNT, 1000);
            const { authToken, sm, om } = tokens;
            setAuthToken(authToken);
            setSoccerMOddsTokens(formatMTokens(sm));
            setOthersMOddsTokens(formatMTokens(om));
            setOddsApiStatus(OddApiStatus.LOADED);
        } catch (error) {
            setOddsApiStatus(OddApiStatus.ERRORED);

            if (error instanceof UnexpectedResponseError) {
                apm.captureError(error);
            }

            if (__ENV_ASI__) {
                window.location.href = getSportsBsiUrl();
            }
            throw error;
        }
    }

    async function clearTokens() {
        setOddsApiStatus(OddApiStatus.NO_TOKEN);
        setAuthToken('');
        setSoccerMOddsTokens(createEmptyMTokens());
        setOthersMOddsTokens(createEmptyMTokens());
    }

    return {
        authToken,
        soccerMOddsTokens,
        othersMOddsTokens,
        oddsApiStatus,
        customerSettings,
        refreshOddsApiTokens,
        clearTokens,
    };
});
